import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import PersonalInfo from '../components/personal-info-view';
import {
  hasIllegalCharacters,
  isValidUrl,
} from 'core/input-validations';
import {timeZoneOptions} from '../assets/timezone-options';
import {localeOptions} from '../assets/locale-options';
import {uiShowToastrMessage} from 'modules/ui-module';
import appconfig from 'config/appconfig';
import {
  meData,
  meIsLoading,
  meRequestPatch,
} from 'modules/me-module';
import {setHandledErrorCodes} from 'lib/error-message-handling';
import castleIOWrapper from 'lib/castleio-tracking';
import { isEqual } from 'lodash';

export class PersonalInfoContainer extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      personData: { ...this.props.personData },
      location: this.props.location,
      errors: {},
    };
    this.LOCATION_FIELD = 'location';
  }

  componentDidMount = () => {
    if(castleIOWrapper && castleIOWrapper.identifyUserAndTrackPageInCastle) {
      castleIOWrapper.identifyUserAndTrackPageInCastle(this.props.personData.id, 'Profile Management Portal - Personal Info');
    }
    if (!this.origState) {
      this.origState = {...this.state};
    }
  };

  contentsHaveChanged = () => {
    if (this.origState && this.state &&
      this.origState.personData &&
      this.state.personData &&
      typeof this.origState.location !== 'undefined' &&
      typeof this.state.location !== 'undefined'
    ) {
      return !(isEqual(this.origState.personData, this.state.personData) &&
        isEqual(this.origState.location, this.state.location));
    }
    return false;
  };

  validateUserInput = (field, value, onBlurCheck) => {
    let errorText = '';
    const errors = {...this.state.errors};
    if (errors[field]) {
      delete errors[field];
    }

    if (field === 'displayName' && value.length > 128) {
      errorText = 'personal-info.name-exceeds-max-length';
    }

    if (field === 'title' && value.length > 250) {
      errorText = 'personal-info.title-exceeds-max-length';
    }

    if (field === 'profileUrl' && (value.length > 250 || (onBlurCheck && value && !isValidUrl(value)))) {
      errorText = 'personal-info.profileUrl-malformed-url';
    }

    if (field === 'location' && value.length > 250) {
      errorText = 'personal-info.location-exceeds-max-length';
    }

    if (!errorText.length && hasIllegalCharacters(value)) {
      errorText = 'shared.illegal-character-entry';
    }

    if (field === 'displayName' && !errorText.length && onBlurCheck && !value.length) {
      errorText = 'personal-info.validation-error-field-name';
    }

    if (errorText.length) {
      errors[field] = errorText;
    }

    this.setState({errors});

    return !errorText.length;
  };

  validateNameEntry = (e) => {
    const field = e.target.id.split('-')[0];
    const value = e.target.value;
    return this.validateUserInput(field, value, true);
  };

  enableSubmit() {
    return (
      Object.keys(this.state.errors).length === 0 &&
      this.contentsHaveChanged() &&
      !this.props.isLoading
    );
  }

  handleLocationEntry = (field, value) => {
    if (this.validateUserInput(field, value, false)) {
      this.setState({location: value});
    }
    return true;
  };

  updatePersonalInfoState = (e) => {
    const personData = {...this.state.personData};
    const field = e.target.id.split('-')[0];
    const value = e.target.value;

    // handle the Location field entry in a dedicated function.
    if (field === this.LOCATION_FIELD) {
      return this.handleLocationEntry(field, value);
    }

    personData[field] = value;
    if (this.validateUserInput(field, value, false)) {
      this.setState({personData});
    }
    return true;
  };

  updateLocaleState = (selection) => {
    const personData = {...this.state.personData};
    // The locale (language) selector control returns the newly selected value and label in an object, not the control itself
    // so we must handle the returned values accordingly.
    if (selection.value && selection.label) {
      personData['locale'] = selection.value;
      personData['selectedLocale'] = selection.value;
      this.setState({personData});
    }
    return true;
  };

  updateTimeZoneState = (selection) => {
    const personData = {...this.state.personData};
    // The time zone selector control returns the newly selected value and label in an object, not the control itself
    // so we must handle the returned values accordingly.
    if (selection.value && selection.label) {
      personData['timezone'] = selection.value;
      this.setState({personData});
    }
    return true;
  };

  handleAjaxResult = (success) => {
    const toastrType = success ? 'success' : 'error';
    const toastrMessageId = success ? 'personal-info.data-save-successful' : 'personal-info.data-save-failed';
    this.props.uiShowToastrMessage(toastrType, toastrMessageId);
  };

  onCancel = (e) => {
    if (typeof e !== 'undefined') {
      e.stopPropagation();
      e.preventDefault();
    }
    this.setState(this.origState);
  };

  onClickSave = (e) => {
    if (typeof e !== 'undefined') {
      e.stopPropagation();
      e.preventDefault();
    }
    const dataToSave = {};
    if (this.state.personData.displayName) {
      dataToSave.displayName = this.state.personData.displayName;
    }
    if (this.state.personData.locale) {
      dataToSave.locale = this.state.personData.locale;
    }
    if (this.state.personData.timezone) {
      dataToSave.timezone = this.state.personData.timezone;
    }

    setHandledErrorCodes([
      'user.username.empty',
      'user.username.maxlength',
      'user.username.invalid',
      'user.locale.malformed',
      'user.locale.language',
      'user.locale.country',
      'user.timezone.notfound',
      'user.givenname.maxlength',
      'user.givenname.invalid',
      'user.familyname.maxlength',
      'user.familyname.invalid',
    ]);

    // only save the optional field values (title, profileUrl, location) if they have changed
    if (this.origState && this.origState.personData) {
      if (this.origState.personData.title !== this.state.personData.title) {
        dataToSave.title = this.state.personData.title || '';
      }
      if (this.origState.personData.profileUrl !== this.state.personData.profileUrl) {
        dataToSave.profileUrl = this.state.personData.profileUrl || '';
      }
      if (this.origState.location !== this.state.location) {
        dataToSave[appconfig.scimSchemas.getGo] = {location: this.state.location};
      }
    }
    this.origState = null;
    return this.props.meRequestPatch(dataToSave)
      .then(
        () => {
          this.handleAjaxResult(true);
          this.origState = {...this.state};
        },
        () => {
          this.handleAjaxResult(false);
        },
      );
  };

  render() {
    return (
      <PersonalInfo
        personData={this.state.personData}
        allLocales={this.props.locales}
        allTimeZones={this.props.timeZones}
        errors={this.state.errors}
        onChange={this.updatePersonalInfoState}
        onBlur={this.validateNameEntry}
        onLocaleChange={this.updateLocaleState}
        onTimeZoneChange={this.updateTimeZoneState}
        onSave={this.onClickSave}
        enableCTAs={this.enableSubmit()}
        onCancel={this.onCancel}
        isLoading={this.props.isLoading}
        locationValue={this.state.location}
      />
    );
  }
}

PersonalInfoContainer.propTypes = {
  personData: PropTypes.object.isRequired,
  locales: PropTypes.array.isRequired,
  timeZones: PropTypes.array.isRequired,
  meRequestPatch: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  location: PropTypes.string.isRequired,
  uiShowToastrMessage: PropTypes.func.isRequired,
};

export const mapStateToProps = (state) => {
  const personData = meData(state);
  personData.title = personData.title || '';
  personData.location = personData.location || '';
  personData.profileUrl = personData.profileUrl || '';
  const location = (personData[appconfig.scimSchemas.getGo] &&
      typeof personData[appconfig.scimSchemas.getGo].location !== 'undefined') ?
    personData[appconfig.scimSchemas.getGo].location : '';
  const locales = localeOptions(personData.locale);
  const timeZones = timeZoneOptions(personData.locale);

  return {
    personData,
    locales,
    timeZones,
    location,
    isLoading: meIsLoading(state),
  };
};

export const mapDispatchToProps = (dispatch) => ({
  meRequestPatch: (payload) => dispatch(meRequestPatch(payload)),
  uiShowToastrMessage: (toastrType, toastrMessageId) => dispatch(uiShowToastrMessage(toastrType, toastrMessageId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PersonalInfoContainer);
